import createClient, { Middleware } from 'openapi-fetch';

// Relative (not the @/ alias): client.ts is bundled standalone for a Node context
// (the e2e web server esbuilds it, no tsconfig path alias) as well as for the browser.
import { DEFAULT_API_BASE_URL } from '../constants';
import type { paths } from './generated/schema';

// Get API base URL from environment.
// An explicit INTEGRATION_BASE_URL override always wins (Node test runtime) so a test can
// target a chosen backend; otherwise the Vite-defined browser value, then a Node fallback.
// The override must precede the Vite value: .env.test sets VITE_API_URL, so checking the
// Vite value first would let that static default shadow the explicit override.
const getBaseUrl = (): string => {
  // Explicit test override (Node runtime) — highest precedence.
  if (
    typeof window === 'undefined' &&
    typeof process !== 'undefined' &&
    process.env?.INTEGRATION_BASE_URL
  ) {
    return process.env.INTEGRATION_BASE_URL;
  }
  // Browser runtime (Vite). Use a define constant instead of import.meta.env so Storybook's
  // IIFE manager build can bundle this module without empty-import-meta warnings.
  if (
    typeof __MINDREALM_VITE_API_URL__ !== 'undefined' &&
    __MINDREALM_VITE_API_URL__
  ) {
    return __MINDREALM_VITE_API_URL__;
  }
  // Node.js fallback. Do not read VITE_API_URL here: .env.test points browser E2E
  // traffic at the Vite same-origin proxy, while Node integration tests should hit
  // the backend harness directly unless INTEGRATION_BASE_URL overrides it.
  if (
    typeof window === 'undefined' &&
    typeof process !== 'undefined' &&
    process.env
  ) {
    return DEFAULT_API_BASE_URL;
  }
  return DEFAULT_API_BASE_URL;
};

const baseUrl = getBaseUrl();

// Middleware to handle 401 errors and redirect to sign-in
const authMiddleware: Middleware = {
  async onResponse({ response }) {
    if (response.status === 401) {
      // JWT expired or invalid - redirect to sign-in with current page as return URL
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        // Don't redirect if on /onboarding - new users won't have an organization yet
        // The onboarding page handles unauthenticated state by showing SignUp
        if (currentPath.startsWith('/onboarding')) {
          return response;
        }
        const fullPath = currentPath + window.location.search;
        window.location.href = `/sign-in?redirect_url=${encodeURIComponent(fullPath)}`;
      }
    }
    return response;
  },
};

export const apiClient = createClient<paths>({
  baseUrl,
});

// Add auth middleware
apiClient.use(authMiddleware);
